// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import {
  fetchCapitalCallNotices,
  generateCapitalCallNotices
} from '../services/capitalCallNoticesService.ts'
import { CapitalCallNotices } from '../../../state_manager/data_models/capital_call_notices.ts'

/** How often the list is re-read while notices are being generated. */
export const NOTICES_POLL_MS = 3000

/**
 * useCapitalCallNotices — the stored notices of one call for the form's
 * Notices section.
 *
 * Re-reads whenever the form re-hydrates (`version`: save, compute, issue,
 * revert) and, while the server is generating, every few seconds until no
 * notice is pending - the generation runs in the background on the server,
 * so the list is the only place its progress shows. A failed poll raises
 * one keyed error toast, taken down by the next read that succeeds.
 *
 * Form-local, like the call detail itself: not in a Redux slice.
 */
export function useCapitalCallNotices(capitalCallId: string | null | undefined, version: unknown) {
  const [notices, setNotices] = useState<CapitalCallNotices | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  // bumped by every poll, successful or not, so a failed one is retried
  const [pollTick, setPollTick] = useState(0)

  const pollToastKey = `capital-call-notices-poll-${capitalCallId ?? ''}`
  // a slow read must not overwrite a later one
  const requestSeq = useRef(0)

  const load = useCallback(
    async (background = false) => {
      if (!capitalCallId) {
        setNotices(null)
        setError(null)
        return
      }
      const seq = ++requestSeq.current
      if (!background) setLoading(true)
      const res = await fetchCapitalCallNotices(capitalCallId, getAccessToken(store.getState().auth))
      if (seq !== requestSeq.current) return
      if (res.data) {
        setNotices(res.data)
        setError(null)
        dismissErrorToast(pollToastKey)
      } else if (background) {
        // keep showing what is there; say the refresh failed
        showErrorToast(res.error || 'Failed to refresh the notices', { key: pollToastKey })
      } else {
        setError(res.error || 'Failed to load the notices')
      }
      if (!background) setLoading(false)
    },
    [capitalCallId, pollToastKey]
  )

  useEffect(() => {
    void load()
  }, [load, version])

  const pending = notices?.prog__summary.pending ?? 0
  useEffect(() => {
    if (pending <= 0) return
    const timer = setTimeout(() => {
      void load(true).then(() => setPollTick((tick) => tick + 1))
    }, NOTICES_POLL_MS)
    return () => clearTimeout(timer)
  }, [pending, pollTick, load])

  useEffect(() => () => dismissErrorToast(pollToastKey), [pollToastKey])

  /** Queue a generation: every LP, or the given commitments. */
  const generate = useCallback(
    async (fundCommitmentIds: string[] | null = null): Promise<{ count: number | null; error?: string }> => {
      if (!capitalCallId) return { count: null, error: 'Save the call first' }
      setGenerating(true)
      try {
        const res = await generateCapitalCallNotices(
          capitalCallId,
          fundCommitmentIds,
          getAccessToken(store.getState().auth)
        )
        await load(true)
        return res
      } finally {
        setGenerating(false)
      }
    },
    [capitalCallId, load]
  )

  const refetch = useCallback(() => load(true), [load])

  return { notices, loading, error, refetch, generate, generating }
}
